import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Alert } from 'antd';
import CustomModal from '../../../../components/Common/CustomModal';
import { useTranslation } from 'react-i18next';
import { useSignature } from '../hooks/useSignature';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface SignatureModalProps {
  visible: boolean;
  onClose: () => void;
  onSignatureSaved?: () => void;
}

const SignatureModal: React.FC<SignatureModalProps> = ({ visible, onClose, onSignatureSaved }) => {
  const { t } = useTranslation();
  const { signature, error, fetchSignature, saveSignature, uploadSignatureImage } = useSignature();
  const [html, setHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const quillRef = useRef<ReactQuill | null>(null);

  useEffect(() => {
    if (visible) {
      fetchSignature();
    }
  }, [visible, fetchSignature]);

  useEffect(() => {
    if (signature?.html !== undefined) {
      setHtml(signature.html);
    } else {
      setHtml('');
    }
  }, [signature]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSignature(html);
      if (typeof onSignatureSaved === 'function') {
        onSignatureSaved();
      }
      onClose();
    } catch (e) {
      alert('Failed to save signature.');
    }
    setSaving(false);
  };

  // Memoized custom image handler for debugging
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: () => {
          try {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.onchange = async () => {
              const file = input.files?.[0];
              if (!file) return;
              try {
                const url = await uploadSignatureImage(file);
                const editor = quillRef.current?.getEditor();
                const range = editor?.getSelection(true);
                if (editor && range) {
                  editor.insertEmbed(range.index, 'image', url);
                  editor.setSelection(range.index + 1);
                }
              } catch (err) {
                alert('Image upload failed.');
              }
            };
            input.click();
          } catch (err) {
            // Log error to help debugging
            console.error('Quill image handler error:', err);
          }
        }
      }
    }
  }), []);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block', 'list', 'bullet', 'link', 'image'
  ];

  return (
    <CustomModal
      title={t('edit_signature')}
      isOpen={visible}
      onClose={onClose}
      onSave={handleSave}
      loading={saving}
      width={"800px"}
    >
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <ReactQuill
        ref={quillRef}
        value={html}
        onChange={setHtml}
        theme="snow"
        modules={modules}
        formats={formats}
        placeholder={t('create_signature_placeholder', 'Create your signature (rich text, images, links...)')}
        style={{ minHeight: 100 }}
      />
    </CustomModal>
  );
};

export default SignatureModal;
